import type {
  KambiSportsSameGameMultiSelection,
  KambiSportsSelection,
  KambiSportsSingleSelection,
} from '@mobi/betslip/types'
import {
  isKambiSportsMultiSelection,
  isKambiSportsSameGameMultiSelection,
  isKambiSportsSingleSelection,
} from '@mobi/betslip/helpers/typeGuards'
import type { KambiValidReward } from '@mobi/api-types/kambi/player/reward'
import type { KambiBettingParams } from '@core/Areas/KambiSports/helpers/api/betting/types'
import { formatDollarDecimalToThousandMultiplier as convertToKambiSystemCurrency } from '@core/Areas/KambiSports/helpers/currencyFormat'

export function createKambiBetRequest({
  selection,
  selectedKambiReward,
  investmentWin,
}: CreateBetRequestParams): Pick<KambiBettingParams, 'bets' | 'couponRows' | 'rewards'> {
  if (isKambiSportsSingleSelection(selection)) {
    return buildRequest(
      {
        bets: [{ couponRowIndexes: [0], stake: convertToKambiSystemCurrency(investmentWin) }],
        couponRows: [buildSingleCouponRow(selection)],
      },
      selectedKambiReward
    )
  }

  if (isKambiSportsSameGameMultiSelection(selection)) {
    return buildRequest(
      {
        bets: [{ couponRowIndexes: [0], stake: convertToKambiSystemCurrency(investmentWin) }],
        couponRows: [buildSameGameMultiCouponRow(selection)],
      },
      selectedKambiReward
    )
  }

  if (isKambiSportsMultiSelection(selection)) {
    const { legs } = selection

    const couponRows: CouponRows = legs.map((leg, index) =>
      isKambiSportsSingleSelection(leg)
        ? buildSingleCouponRow(leg, index)
        : buildSameGameMultiCouponRow(leg, index)
    )

    return buildRequest(
      {
        bets: [
          {
            couponRowIndexes: couponRows.map((_, index) => index),
            stake: convertToKambiSystemCurrency(investmentWin),
          },
        ],
        couponRows,
      },
      selectedKambiReward
    )
  }

  return { couponRows: [], bets: [] }
}

// =============
// Local Helpers
// =============

function buildSingleCouponRow(selection: KambiSportsSingleSelection, index = 0): CouponRows[0] {
  const { odds, id } = selection.betOffer.outcomes[0]
  return { index, odds, outcomeId: id, type: 'SIMPLE' }
}

function buildSameGameMultiCouponRow(
  selection: KambiSportsSameGameMultiSelection,
  index = 0
): CouponRows[0] {
  const { betOffers, odds } = selection
  return {
    index,
    odds,
    type: 'BET_BUILDER',
    group: {
      operation: 'AND',
      groups: betOffers.map(({ outcomes }) => ({
        operation: 'AND',
        outcomeIds: [outcomes[0].id],
      })),
    },
  }
}

function buildRequest(
  base: Omit<Pick<KambiBettingParams, 'bets' | 'couponRows' | 'rewards'>, 'rewards'>,
  selectionReward?: KambiValidReward | null
) {
  return selectionReward
    ? {
        ...base,
        rewards: [
          {
            rewardType: selectionReward.rewardType,
            rewardId:
              selectionReward.isGroupReward && selectionReward.groupRewardId
                ? selectionReward.groupRewardId
                : selectionReward.rewardId,
          },
        ],
      }
    : base
}

// =====
// Types
// =====

type CreateBetRequestParams = {
  selection: KambiSportsSelection
  selectedKambiReward?: KambiValidReward | null
  investmentWin: number
}

type CouponRows = KambiBettingParams['couponRows']
